import React, { useState, useEffect, useCallback, useMemo, useLayoutEffect, useRef } from "react";
import MetaTags from "react-meta-tags";
import Select from "react-select";
import axios from "../api/axios";
import jwt_decode from "jwt-decode";
import { SERVER_URL } from "../ServerLink";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment-timezone";
import {
  Table, Row, Col, Card, CardBody, Button, Modal,
  CardHeader, ModalHeader, ModalBody, ModalFooter,
  Pagination, PaginationItem, PaginationLink,
  Label, Input, Form, Collapse, Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const DENVER_TZ = "America/Denver";

const STATUS_OPTIONS = [
  { value: "Pending",      label: "Pending" },
  { value: "Signed Lease", label: "Signed Lease" },
  { value: "No show",      label: "No show" },
  { value: "Scheduled",    label: "Scheduled" },
  { value: "Not Approved", label: "Not Approved" },
];

const STATUS_COLOR_MAP = {
  "Pending":      { bg: "#ffc107", color: "#000" },
  "Signed Lease": { bg: "#198754", color: "#fff" },
  "Scheduled":    { bg: "#0d6efd", color: "#fff" },
  "No show":      { bg: "#dc3545", color: "#fff" },
  "Not Approved": { bg: "#dc3545", color: "#fff" },
};

const DEFAULT_STATUS_COLORS = STATUS_COLOR_MAP["Pending"];

// Portal the react-select menu to <body> so an open dropdown floats above the
// table instead of overlapping/hiding behind it.
const SELECT_MENU_STYLES = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const roundUpTo15 = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const rounded = Math.ceil(m / 15) * 15;
  if (rounded === 60) return `${String((h + 1) % 24).padStart(2, "0")}:00`;
  return `${String(h).padStart(2, "0")}:${String(rounded).padStart(2, "0")}`;
};

const getTodayDenver = () => moment().tz(DENVER_TZ).format("YYYY-MM-DD");

const slugifyTitle = (title) =>
  String(title || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const buildPropertyLink = (companyDomain, propertyTitle) => {
  if (!companyDomain || !propertyTitle) return "";
  const slug = slugifyTitle(propertyTitle);
  if (!slug) return "";
  const base = companyDomain.endsWith("/") ? companyDomain : `${companyDomain}/`;
  return `${base}properties/${slug}/`;
};

const formatHistoryTime = (dateLike) => {
  if (!dateLike) return "";
  return moment(dateLike).tz(DENVER_TZ).format("MMM D, YYYY h:mm A");
};

// The desktop table pins ~640px of sticky columns (#, prospect, property on the
// left; actions on the right). Below this container width there is no room
// left to scroll the other nine columns, so we switch to a card layout instead.
const COMPACT_BREAKPOINT = 1100;

/** Width of the element the returned ref is attached to; tracks the sidebar and orientation. */
function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () =>
      setWidth(el.getBoundingClientRect().width || el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  return [ref, width];
}

const initialsOf = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const Appointment = () => {
  const decode = jwt_decode(window.localStorage.accessToken);
  const decode2 = jwt_decode(window.localStorage.getItem("accessToken"));
  const authUser = JSON.parse(window.localStorage.getItem("authUser"));
  const companyId = authUser.userData.company_id;
  const managerDisplayName = [
    authUser?.userData?.firstname,
    authUser?.userData?.lastname,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || authUser?.userData?.email || "Property Manager";

  const [booked, setBooked] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [startPage, setStartPage] = useState(1);
  const [pages, setPages] = useState([]);
  const PAGE_LIMIT = 10;

  const [filterOpen, setFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [property, setProperty] = useState(null);
  const [reason, setReason] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [propertyList, setPropertyList] = useState([]);
  const [reasonList, setReasonList] = useState([]);

  const [stats, setStats] = useState({ total: 0, byStatusUpdate: {} });

  const [statusTypeList, setStatusTypeList] = useState([]);

  const [confirmModal, setConfirmModal] = useState(false);
  const [pendingApptId, setPendingApptId] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState("");
  const [rejectType, setRejectType] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [reasonErr, setReasonErr] = useState("");

  const [acceptModal, setAcceptModal] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState("");
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideStart, setOverrideStart] = useState("");
  const [overrideEnd, setOverrideEnd] = useState("");
  const [acceptManagerComments, setAcceptManagerComments] = useState("");
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [disableSyncButton, setDisableSyncButton] = useState(false);

  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptSlotOptions, setAcceptSlotOptions] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [noteDraftMap, setNoteDraftMap] = useState({});
  const [savingNoteId, setSavingNoteId] = useState("");
  const [openNoteHistoryId, setOpenNoteHistoryId] = useState("");

  const [textModal, setTextModal] = useState(false);
  const [textTarget, setTextTarget] = useState(null);
  const [textBody, setTextBody] = useState("");
  const [textTemplateIdx, setTextTemplateIdx] = useState(0);
  const [sendingText, setSendingText] = useState(false);
  const [showTextHistory, setShowTextHistory] = useState(false);

  const pageTotal = Math.ceil(stats.total / PAGE_LIMIT);

  const statCards = useMemo(() => {
    const by = stats.byStatusUpdate || {};
    return [
      { label: "Total", count: stats.total ?? total, accent: "#0f172a", bg: "#f1f5f9", icon: "📋" },
      { label: "Pending", count: by["Pending"] || 0, accent: "#b45309", bg: "#fffbeb", icon: "⏳" },
      { label: "Scheduled", count: by["Scheduled"] || 0, accent: "#1d4ed8", bg: "#eff6ff", icon: "📅" },
      { label: "Signed Lease", count: by["Signed Lease"] || 0, accent: "#15803d", bg: "#ecfdf5", icon: "✍" },
      { label: "No show", count: by["No show"] || 0, accent: "#b91c1c", bg: "#fef2f2", icon: "🚫" },
      { label: "Not Approved", count: by["Not Approved"] || 0, accent: "#9f1239", bg: "#fff1f2", icon: "✕" },
    ];
  }, [stats, total]);

  const convertToDenverTime = (utcTime) =>
    new Date(utcTime).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "numeric", hour12: true,
      timeZone: DENVER_TZ,
    });

  const toDenverHHmm = (utcTime) =>
    moment(utcTime).tz(DENVER_TZ).format("HH:mm");

  const toDenverDate = (utcTime) =>
    moment(utcTime).tz(DENVER_TZ).format("YYYY-MM-DD");

  const denverToISO = (dateStr, timeStr) =>
    moment.tz(`${dateStr} ${timeStr}`, "YYYY-MM-DD HH:mm", DENVER_TZ).toISOString();

  const parseAmPmToHHmm = (s) => {
    const m = (s || "").trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10) % 12;
    if (/p/i.test(m[3])) h += 12;
    return `${String(h).padStart(2, "0")}:${m[2]}`;
  };

  const fetchAcceptSlots = useCallback(async () => {
    if (!acceptModal || !acceptTarget || !overrideDate) return;
    const original = booked.find((b) => b._id === acceptTarget);
    if (!original) return;

    const propertyId =
      original?.propertyId ||
      original?.properties?.[0]?._id ||
      original?.properties?.[0]?.id;
    if (!propertyId) {
      setAcceptSlotOptions([]);
      return;
    }

    setLoadingSlots(true);
    try {
      const res = await fetch(SERVER_URL + "/calender/available-slots", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: overrideDate,
          propertyId,
          managerId: decode2.id,
        }),
      });
      const data = await res.json();

      const fromApi = (data.available_slots || [])
        .map((slot) => {
          const [s, e] = slot.split(" - ").map((p) => parseAmPmToHHmm(p));
          return s && e ? { start: s, end: e } : null;
        })
        .filter(Boolean);

      const origDate = toDenverDate(original.startTime);
      if (origDate === overrideDate) {
        const origStart = roundUpTo15(toDenverHHmm(original.startTime));
        const origEnd = roundUpTo15(toDenverHHmm(original.endTime));
        if (!fromApi.some((s) => s.start === origStart)) {
          fromApi.push({ start: origStart, end: origEnd });
        }
      }

      fromApi.sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
      setAcceptSlotOptions(fromApi);
    } catch (err) {
      console.error(err);
      setAcceptSlotOptions([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [acceptModal, acceptTarget, overrideDate, booked, decode2.id]);

  useEffect(() => {
    fetchAcceptSlots();
  }, [fetchAcceptSlots]);

  const startTimeOptions = useMemo(() => {
    const set = new Set(acceptSlotOptions.map((s) => s.start));
    return Array.from(set).sort();
  }, [acceptSlotOptions]);

  const endTimeOptions = useMemo(() => {
    if (!overrideStart) return [];
    const set = new Set(
      acceptSlotOptions
        .map((s) => s.end)
        .filter((end) => end > overrideStart)
    );
    return Array.from(set).sort();
  }, [acceptSlotOptions, overrideStart]);

  useEffect(() => {
    if (!overrideStart) return;
    if (endTimeOptions.length === 0) {
      if (overrideEnd) setOverrideEnd("");
      return;
    }
    if (!endTimeOptions.includes(overrideEnd)) {
      setOverrideEnd(endTimeOptions[0]);
    }
  }, [overrideStart, endTimeOptions, overrideEnd]);

  const fetchStatusTypes = useCallback(async () => {
    try {
      const { data } = await axios.post("appointmentStatus/getStatus", {
        company_id: companyId ?? decode.id,
      });
      if (data.appointmentStatus) {
        setStatusTypeList([...data.appointmentStatus].reverse());
      }
    } catch (err) {
      console.error(err);
    }
  }, [companyId, decode.id]);

  const fetchAppointments = useCallback(
    async (type) => {
      const page = type === "filter" ? 1 : pageNumber;

      let query =
        type === "clear"
          ? { pageNumber: 1, manager_id: decode2.id }
          : {
            companyDomain: decode?.domain,
            pageNumber: page,
            manager_id: decode2.id,
            startDate,
            endDate,
            property: property?.value,
            reasonId: reason?.value,
            status: statusFilter?.value,
          };

      try {
        const res = await fetch(SERVER_URL + "/user_appointment/get-pending-slot", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(query),
        });
        const data = await res.json();

        setBooked(data?.bookedSlot || []);
        setTotal(data?.totalCount || data?.total?.Total || 0);
        setStats({
          total: data?.total?.Total || 0,
          byStatusUpdate: data?.total?.byStatusUpdate || {},
        });
        setPropertyList((data?.properties || []).map((p) => ({ label: p.title, value: p._id })));
        setReasonList((data?.reasons || []).map((r) => ({ label: r.reasonType, value: r._id })));
      } catch (err) {
        console.error(err);
        toast.error("Failed to load appointments.");
      }
    },
    [pageNumber, decode?.domain, decode2.id, startDate, endDate, property, reason, statusFilter]
  );

  const checkOutlookStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`/outlook/status/${decode2.id}`);
      setOutlookConnected(!!data.connected);
    } catch {
      setOutlookConnected(false);
    }
  }, [decode2.id]);

  const checkGoogleTokenExpiry = useCallback(async () => {
    try {
      const { data, status } = await axios.post(`/calender/getExpirationTime/${decode.id}`);
      if (status === 200) {
        const expiry = parseInt(data.token.expiresAt);
        setDisableSyncButton(!expiry || Date.now() > expiry);
      } else {
        setDisableSyncButton(true);
      }
    } catch {
      setDisableSyncButton(true);
    }
  }, [decode.id]);

  useEffect(() => {
    fetchStatusTypes();
  }, [fetchStatusTypes]);

  useEffect(() => {
    fetchAppointments();
  }, [pageNumber]);

  useEffect(() => {
    checkGoogleTokenExpiry();
    checkOutlookStatus();
  }, []);

  const currentDateUTC = new Date().toISOString();
  const filteredBooked = booked.map((item) =>
    currentDateUTC >= item.endTime
      ? { ...item, status: "Appointment expired" }
      : item
  );

  const openAcceptModal = (item) => {
    setAcceptTarget(item._id);
    const rawDate  = toDenverDate(item.startTime);
    const rawStart = roundUpTo15(toDenverHHmm(item.startTime));
    const rawEnd   = roundUpTo15(toDenverHHmm(item.endTime));
    setOverrideDate(rawDate);
    setOverrideStart(rawStart);
    setOverrideEnd(rawEnd);
    setAcceptManagerComments(item.managerComments || "");
    setIsAccepting(false);
    setAcceptModal(true);
  };

  const submitAccept = async () => {
    const originalItem = booked.find((b) => b._id === acceptTarget);
    if (!originalItem) return;

    const originalDate  = toDenverDate(originalItem.startTime);
    const originalStart = roundUpTo15(toDenverHHmm(originalItem.startTime));
    const originalEnd   = roundUpTo15(toDenverHHmm(originalItem.endTime));

    const dateChanged  = overrideDate  !== originalDate;
    const startChanged = overrideStart !== originalStart;
    const endChanged   = overrideEnd   !== originalEnd;
    const timeChanged  = dateChanged || startChanged || endChanged;

    const willSyncGoogle  = !disableSyncButton;
    const willSyncOutlook = outlookConnected;

    const payload = {
      slot_id: acceptTarget,
      userId: decode2.id,
      syncOutlook: willSyncOutlook,
      managerComments: acceptManagerComments,
      ...(timeChanged && {
        overrideDate: overrideDate,
        overrideStartTime: denverToISO(overrideDate, overrideStart),
        overrideEndTime:   denverToISO(overrideDate, overrideEnd),
      }),
    };

    setIsAccepting(true);
    try {
      const res = await fetch(SERVER_URL + "/user_appointment/accept-appointment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.status === 200) {
        fetchAppointments();
        setAcceptModal(false);

        if (willSyncGoogle) {
          await syncWithGoogle(acceptTarget, data.event);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsAccepting(false);
    }
  };

  const syncWithGoogle = async (id, event) => {
    const filteredEvent = filteredBooked.find((e) => e._id === id);
    if (!filteredEvent) return;

    const convertToLocalTime = (utcTime) => {
      const d = utcTime.includes("T") ? new Date(utcTime) : new Date(`1970-01-01T${utcTime}`);
      return d.toLocaleTimeString(undefined, {
        hour: "numeric", minute: "numeric", hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    };

    let role = decode2.role;
    if (role === "manager" || role === "company") role = "PropertyManager";
    else if (role === "technical staff") role = "technicalStaff";

    try {
      toast("Syncing with Google Calendar…");
      const { data: calData } = await axios.post("/calender/create-event", {
        userId: decode2.id,
        day: moment(event.date.split("T")[0]).format("ddd"),
        eventDate: event.date.split("T")[0],
        StartTime: convertToLocalTime(event.startTime),
        endTime: convertToLocalTime(event.endTime),
        createdBy: decode2.id,
        type: filteredEvent.reasonType[0]?.reasonType,
        propertyId: filteredEvent.properties[0]?.title,
        companyDomain: decode2?.managerOf || decode2?.domain,
        title: event.name,
        description: event.description,
        event_id: id,
        role,
        available: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (calData?.status === 200) toast.success("Synced to Google Calendar.");
    } catch (err) {
      console.error(err);
    }
  };

  const updateAppointment = async (id, status) => {
    if ((status === "rejected" || status === "canceled") && !rejectReason.trim()) {
      setReasonErr("Manager comments are required");
      return;
    }
    setReasonErr("");
    try {
      const res = await fetch(SERVER_URL + "/user_appointment/update-event-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: id,
          status,
          reason: rejectReason,
          managerComments: rejectReason,
        }),
      });
      const data = await res.json();
      if (data.status === 200) {
        fetchAppointments();
        setRejectModal(false);
        toast.error(`Appointment ${status}.`);

        if (status === "canceled") {
          try {
            const { data: delData } = await axios.post("/calender/delete-event", {
              userId: decode.id, event_id: id,
            });
            if (delData?.status === 200) toast.success(delData.message);
          } catch (err) {
            console.error(err);
          }
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update appointment.");
    }
  };

  const changeStatus = async () => {
    try {
      const { data, status } = await axios.post("/user_appointment/update_status", {
        apptId: pendingApptId, status: pendingStatus,
      });
      if (status === 200 && data.message === "Appointment status updated successfully") {
        setBooked((prev) =>
          prev.map((b) =>
            b._id === pendingApptId ? { ...b, statusUpdate: pendingStatus } : b
          )
        );
        setConfirmModal(false);
        fetchAppointments();
      }
    } catch (err) {
      console.error("Unable to update status:", err);
    }
  };

  const saveNote = async (item) => {
    const draft = (noteDraftMap[item._id] || "").trim();
    if (!draft) {
      toast.warn("Note is empty.");
      return;
    }
    setSavingNoteId(item._id);
    try {
      const res = await fetch(SERVER_URL + "/user_appointment/add-note", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: item._id,
          note: draft,
          userId: decode2.id,
          userName: managerDisplayName,
        }),
      });
      const data = await res.json();
      if (data.status === 200) {
        setBooked((prev) =>
          prev.map((b) =>
            b._id === item._id ? { ...b, notesHistory: data.notesHistory } : b
          )
        );
        setNoteDraftMap((prev) => ({ ...prev, [item._id]: "" }));
        toast.success("Note saved.");
      } else {
        toast.error(data.message || "Failed to save note.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save note.");
    } finally {
      setSavingNoteId("");
    }
  };

  const buildTextTemplates = (item) => {
    const propertyTitle = item?.properties?.[0]?.title || "the property";
    const propertyLink = buildPropertyLink(
      item?.companyDomain,
      item?.properties?.[0]?.title
    );
    const dateLabel = item?.startTime
      ? moment(item.startTime).tz(DENVER_TZ).format("ddd, MMM D")
      : "the scheduled date";
    const timeLabel = item?.startTime && item?.endTime
      ? `${convertToDenverTime(item.startTime)} – ${convertToDenverTime(item.endTime)}`
      : "the scheduled time";
    const firstName = (item?.name || "").split(/\s+/)[0] || "there";
    const linkLine = propertyLink ? `\n\nProperty details: ${propertyLink}` : "";
    const managerLine = managerDisplayName ? `— ${managerDisplayName}` : "— Your Property Team";

    return [
      {
        label: "Friendly reminder",
        body:
          `Hi ${firstName}! 👋 Just a quick reminder about your appointment for ${propertyTitle} on ${dateLabel} at ${timeLabel}. ` +
          `We're looking forward to meeting you!${linkLine}\n\n${managerLine}`,
      },
      {
        label: "Confirm attendance",
        body:
          `Hi ${firstName}, this is ${managerDisplayName} from your property team. ` +
          `Can you confirm you're still able to make it to ${propertyTitle} on ${dateLabel} at ${timeLabel}? ` +
          `A quick yes/no reply is perfect.${linkLine}\n\nThank you!`,
      },
      {
        label: "Share property link",
        body:
          `Hi ${firstName}! Thanks for booking with us. ` +
          `Here are the details for ${propertyTitle} ahead of your visit on ${dateLabel}:${linkLine ? linkLine : "\n\n(Property link unavailable)"}\n\n` +
          `Reach out anytime if you have questions.\n${managerLine}`,
      },
    ];
  };

  const openTextModal = (item) => {
    setTextTarget(item);
    const templates = buildTextTemplates(item);
    setTextTemplateIdx(0);
    setTextBody(templates[0]?.body || "");
    setShowTextHistory(false);
    setTextModal(true);
  };

  const handleTemplateChange = (idx) => {
    if (!textTarget) return;
    const templates = buildTextTemplates(textTarget);
    setTextTemplateIdx(idx);
    setTextBody(templates[idx]?.body || "");
  };

  const sendText = async () => {
    if (!textTarget) return;
    const body = (textBody || "").trim();
    if (!body) {
      toast.warn("Message body cannot be empty.");
      return;
    }
    if (textTarget.textSent || (textTarget.textHistory?.length || 0) > 0) {
      toast.error("A text has already been sent to this prospect.");
      return;
    }
    setSendingText(true);
    try {
      const res = await fetch(SERVER_URL + "/user_appointment/send-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slot_id: textTarget._id,
          message: body,
          userId: decode2.id,
          userName: managerDisplayName,
        }),
      });
      const data = await res.json();
      if (data.status === 200) {
        setBooked((prev) =>
          prev.map((b) =>
            b._id === textTarget._id
              ? { ...b, textHistory: data.textHistory, textSent: true }
              : b
          )
        );
        setTextTarget((prev) =>
          prev ? { ...prev, textHistory: data.textHistory, textSent: true } : prev
        );
        setShowTextHistory(true);
        toast.success("Text sent to prospect.");
      } else if (data.status === 409) {
        setBooked((prev) =>
          prev.map((b) =>
            b._id === textTarget._id
              ? { ...b, textHistory: data.textHistory || [], textSent: true }
              : b
          )
        );
        setTextTarget((prev) =>
          prev ? { ...prev, textHistory: data.textHistory || [], textSent: true } : prev
        );
        setShowTextHistory(true);
        toast.error(data.message || "Text already sent.");
      } else {
        toast.error(data.message || "Failed to send text.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to send text.");
    } finally {
      setSendingText(false);
    }
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
    if (page > startPage + PAGE_LIMIT - 1 || page < startPage) {
      setStartPage(Math.max(1, page - Math.floor(PAGE_LIMIT / 2)));
    }
  };

  const clearFilters = () => {
    setStartDate(""); setEndDate("");
    setProperty(null); setReason(null); setStatusFilter(null);
    setFilterOpen(false);
    fetchAppointments("clear");
  };

  const [listRef, listWidth] = useElementWidth();
  const isCompact = listWidth > 0 && listWidth < COMPACT_BREAKPOINT;

  // ── Cell renderers shared by the desktop table and the compact (tablet) cards ──
  const renderProspect = (item) => (
    <div className="apt-prospect">
      <div className="apt-avatar">{initialsOf(item.name)}</div>
      <div className="apt-prospect-info">
        <div className="apt-prospect-name">{item.name || "Unknown"}</div>
        {item.email && (
          <div className="apt-prospect-meta">
            <a className="apt-prospect-link" href={`mailto:${item.email}`}>✉ {item.email}</a>
          </div>
        )}
        {item.phone && (
          <div className="apt-prospect-meta">
            <a className="apt-prospect-link" href={`tel:${item.phone}`}>☎ {item.phone}</a>
            {item.phone1 ? `, ${item.phone1}` : ""}
          </div>
        )}
      </div>
    </div>
  );

  const renderProperty = (item) => (
    <>
      <div className="apt-property-name">
        {item.properties?.length > 0 ? item.properties[0].title : "—"}
      </div>
      {item.layouts?.length > 0 && (
        <div className="apt-property-layout">{item.layouts[0].layoutName}</div>
      )}
    </>
  );

  const renderReason = (item) =>
    item.reasonType?.length > 0 ? (
      <span className="apt-pill apt-pill--neutral">{item.reasonType[0].reasonType}</span>
    ) : (
      <span className="apt-empty">—</span>
    );

  const renderComment = (text, kind) =>
    text ? (
      <div className={`apt-comment apt-comment--${kind}`}>{text}</div>
    ) : (
      <span className="apt-empty">—</span>
    );

  const renderNotes = (item) => {
    const history = item.notesHistory || [];
    const isHistoryOpen = openNoteHistoryId === item._id;
    const visible = isHistoryOpen ? history : history.slice(-2);
    return (
      <div className="apt-notes-box">
        {history.length > 0 ? (
          <div className="apt-notes-list">
            {visible.slice().reverse().map((n, i) => (
              <div className="apt-note-item" key={i}>
                <div className="apt-note-meta">
                  <span className="apt-note-author">{n.createdByName || "Manager"}</span>
                  <span className="apt-note-time">{formatHistoryTime(n.createdAt)}</span>
                </div>
                <div className="apt-note-text">{n.note}</div>
              </div>
            ))}
            {history.length > 2 && (
              <button
                type="button"
                className="apt-note-toggle"
                onClick={() => setOpenNoteHistoryId(isHistoryOpen ? "" : item._id)}
              >
                {isHistoryOpen ? "Hide history" : `Show all (${history.length})`}
              </button>
            )}
          </div>
        ) : (
          <span className="apt-empty">No notes yet</span>
        )}
        <div className="apt-note-editor">
          <textarea
            rows={2}
            className="apt-note-input"
            placeholder="Add a note…"
            value={noteDraftMap[item._id] || ""}
            onChange={(e) =>
              setNoteDraftMap((prev) => ({ ...prev, [item._id]: e.target.value }))
            }
            disabled={savingNoteId === item._id}
          />
          <button
            type="button"
            className="apt-note-save"
            onClick={() => saveNote(item)}
            disabled={savingNoteId === item._id || !(noteDraftMap[item._id] || "").trim()}
          >
            {savingNoteId === item._id ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    );
  };

  const renderStatusSelect = (item, { compact = false } = {}) => {
    const current = item?.statusUpdate || "Pending";
    const colors = STATUS_COLOR_MAP[current] || DEFAULT_STATUS_COLORS;
    const options = statusTypeList.map((s) => ({ label: s.status, value: s.status }));
    const selectedOption =
      options.find((o) => o.value === current) ||
      (current === "Pending" ? { label: "Pending", value: "Pending" } : null);
    // Touch-friendly sizing on the compact layout; the desktop table keeps the slim pill.
    const h = compact ? 34 : 26;
    const fs = compact ? "0.8rem" : "0.7rem";
    const customStyles = {
      control: (base, state) => ({
        ...base,
        minWidth: compact ? 130 : 80,
        minHeight: h,
        height: h,
        borderRadius: h / 2,
        border: "none",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(0,0,0,0.12)" : "0 1px 3px rgba(0,0,0,0.15)",
        backgroundColor: colors.bg,
        cursor: "pointer",
        padding: 0,
      }),
      valueContainer: (base) => ({ ...base, height: h, padding: compact ? "0 10px" : "0 6px" }),
      singleValue: (base) => ({ ...base, color: colors.color, fontWeight: 600, fontSize: fs, margin: 0 }),
      placeholder: (base) => ({ ...base, color: colors.color, fontWeight: 600, fontSize: fs, margin: 0 }),
      dropdownIndicator: (base) => ({
        ...base,
        color: colors.color,
        padding: compact ? "0 8px" : "0 4px",
        svg: { width: compact ? 14 : 10, height: compact ? 14 : 10 },
        "&:hover": { color: colors.color },
      }),
      indicatorSeparator: () => ({ display: "none" }),
      menu: (base) => ({
        ...base,
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: "0 4px 14px rgba(0,0,0,0.13)",
        zIndex: 9999,
        minWidth: compact ? 160 : 125,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => {
        const c = STATUS_COLOR_MAP[state.data.value];
        return {
          ...base,
          backgroundColor: state.isFocused ? (c ? c.bg : "#6c757d") : "#fff",
          color: state.isFocused ? (c ? c.color : "#fff") : "#333",
          fontWeight: 500,
          fontSize: compact ? "0.82rem" : "0.72rem",
          padding: compact ? "10px 14px" : "4px 10px",
          cursor: "pointer",
          transition: "background 0.15s",
        };
      },
    };
    return (
      <Select
        options={options.filter((o) => o.value !== "Pending")}
        value={selectedOption}
        placeholder="Pending"
        styles={customStyles}
        isSearchable={false}
        onChange={(opt) => {
          setPendingApptId(item._id);
          setPendingStatus(opt.value);
          setConfirmModal(true);
        }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    );
  };

  const renderActions = (item) => {
    const texted = item.textSent || (item.textHistory?.length || 0) > 0;
    return (
      <>
        {item.status === "pending" && (
          <>
            <button
              type="button"
              className="apt-action apt-action--accept"
              onClick={() => openAcceptModal(item)}
              title="Accept appointment"
            >
              ✓ Accept
            </button>
            <button
              type="button"
              className="apt-action apt-action--reject"
              onClick={() => {
                setRejectTarget(item._id);
                setRejectType("rejected");
                setRejectReason("");
                setReasonErr("");
                setRejectModal(true);
              }}
              title="Reject appointment"
            >
              ✕ Reject
            </button>
          </>
        )}
        {item.status === "booked" && (
          <>
            <span className="apt-action apt-action--booked" title="Already booked">✓ Booked</span>
            <button
              type="button"
              className="apt-action apt-action--reject"
              onClick={() => {
                setRejectTarget(item._id);
                setRejectType("canceled");
                setRejectReason("");
                setReasonErr("");
                setRejectModal(true);
              }}
              title="Cancel appointment"
            >
              ✕ Cancel
            </button>
          </>
        )}
        {item.status !== "pending" && item.status !== "booked" && (
          <span className="apt-action apt-action--disabled">{item.status}</span>
        )}
        <button
          type="button"
          className={`apt-action apt-action--text${texted ? " apt-action--text-sent" : ""}`}
          onClick={() => openTextModal(item)}
          title={texted ? "View text history" : "Send text to prospect"}
        >
          {texted ? "✓ Texted" : "✉ Text"}
        </button>
      </>
    );
  };

  const scheduleFacts = (item) => [
    { label: "Date", value: moment(item.date).utc().format("YYYY-MM-DD") },
    { label: "Time", value: `${convertToDenverTime(item.startTime)} – ${convertToDenverTime(item.endTime)}` },
    {
      label: "Received",
      value: item.createdAt ? moment(item.createdAt).tz(DENVER_TZ).format("MMM D, YYYY h:mm A") : "—",
    },
    { label: "Move in", value: item.moveInDate ? moment(item.moveInDate).utc().format("YYYY-MM-DD") : "—" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags><title>Rentdigicare | Appointments</title></MetaTags>
        <ToastContainer />
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Appointments" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardHeader className="apt-header">
                  <div className="apt-header-row">
                    <div>
                      <div className="apt-eyebrow">Operations</div>
                      <h4 className="apt-title">Appointments</h4>
                    </div>
                    <button
                      onClick={() => setFilterOpen(!filterOpen)}
                      className={`apt-filter-toggle ${filterOpen ? "is-open" : ""}`}
                      type="button"
                    >
                      <span className="apt-filter-icon">⚙</span> Filters
                      {(startDate || endDate || property || reason || statusFilter) && (
                        <span className="apt-filter-dot" />
                      )}
                    </button>
                  </div>

                  <div className="apt-stats">
                    {statCards.map(({ label, count, accent, bg, icon }) => (
                      <div key={label} className="apt-stat-card" style={{ background: bg }}>
                        <div className="apt-stat-icon" style={{ color: accent }}>{icon}</div>
                        <div>
                          <div className="apt-stat-label" style={{ color: accent }}>{label}</div>
                          <div className="apt-stat-count">{count}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Collapse isOpen={filterOpen}>
                    <div className="apt-filter-panel">
                      <Form>
                        <div className="row g-3">
                          {[
                            { label: "From", type: "date", value: startDate, onChange: setStartDate },
                            { label: "To", type: "date", value: endDate, onChange: setEndDate },
                          ].map(({ label, type, value, onChange }) => (
                            <div className="col-sm-6 col-lg-3" key={label}>
                              <Label className="apt-filter-label">{label}</Label>
                              <Input
                                className="form-control form-control-sm"
                                type={type}
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                              />
                            </div>
                          ))}
                          <div className="col-sm-6 col-lg-3">
                            <Label className="apt-filter-label">Property</Label>
                            <Select value={property} onChange={setProperty} options={propertyList} placeholder="Any property" isClearable menuPortalTarget={typeof document !== "undefined" ? document.body : null} menuPosition="fixed" styles={SELECT_MENU_STYLES} />
                          </div>
                          <div className="col-sm-6 col-lg-3">
                            <Label className="apt-filter-label">Reason</Label>
                            <Select value={reason} onChange={setReason} options={reasonList} placeholder="Any reason" isClearable menuPortalTarget={typeof document !== "undefined" ? document.body : null} menuPosition="fixed" styles={SELECT_MENU_STYLES} />
                          </div>
                          <div className="col-sm-6 col-lg-3">
                            <Label className="apt-filter-label">Status</Label>
                            <Select value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} placeholder="Any status" isClearable menuPortalTarget={typeof document !== "undefined" ? document.body : null} menuPosition="fixed" styles={SELECT_MENU_STYLES} />
                          </div>
                          <div className="col-12 d-flex align-items-end justify-content-end gap-2 apt-filter-actions">
                            <button type="button" className="apt-btn apt-btn--ghost" onClick={clearFilters}>
                              Clear
                            </button>
                            <button type="button" className="apt-btn apt-btn--primary" onClick={() => fetchAppointments("filter")}>
                              Apply filters
                            </button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  </Collapse>
                </CardHeader>

                <CardBody className="apt-body">
                  <div ref={listRef} className="apt-list">
                    {isCompact ? (
                      <div className="apt-cards">
                        {filteredBooked.length === 0 && (
                          <div className="apt-cards-empty">No appointments found.</div>
                        )}
                        {filteredBooked.map((item, index) => (
                          <article className="apt-card" key={item._id || index}>
                            <header className="apt-card-head">
                              <span className="apt-card-num">
                                {(pageNumber - 1) * PAGE_LIMIT + index + 1}
                              </span>
                              <div className="apt-card-identity">
                                {renderProspect(item)}
                                <div className="apt-card-property">{renderProperty(item)}</div>
                              </div>
                              <div className="apt-card-status">
                                {renderStatusSelect(item, { compact: true })}
                              </div>
                            </header>

                            <dl className="apt-card-facts">
                              {scheduleFacts(item).map((f) => (
                                <div className="apt-fact" key={f.label}>
                                  <dt>{f.label}</dt>
                                  <dd>{f.value}</dd>
                                </div>
                              ))}
                              <div className="apt-fact">
                                <dt>Reason</dt>
                                <dd>{renderReason(item)}</dd>
                              </div>
                            </dl>

                            <div className="apt-card-body">
                              <section className="apt-card-section">
                                <h6 className="apt-card-label">Prospect comment</h6>
                                {renderComment(item.description, "prospect")}
                                <h6 className="apt-card-label">Manager comment</h6>
                                {renderComment(item.managerComments, "manager")}
                              </section>
                              <section className="apt-card-section">
                                <h6 className="apt-card-label">Notes</h6>
                                {renderNotes(item)}
                              </section>
                            </div>

                            <footer className="apt-card-actions">{renderActions(item)}</footer>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <div className="table-responsive apt-table-wrap">
                        <Table className="apt-table mb-0">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Prospect</th>
                              <th>Property</th>
                              <th>Reason</th>
                              <th>Prospect Comment</th>
                              <th>Manager Comment</th>
                              <th>Notes</th>
                              <th>Status</th>
                              <th>Time Slot</th>
                              <th>Date</th>
                              <th>Received On</th>
                              <th>Move In</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBooked.map((item, index) => {
                              const facts = scheduleFacts(item);
                              return (
                                <tr key={item._id || index}>
                                  <td className="apt-cell-num">
                                    {(pageNumber - 1) * PAGE_LIMIT + index + 1}
                                  </td>
                                  <td>{renderProspect(item)}</td>
                                  <td>{renderProperty(item)}</td>
                                  <td>{renderReason(item)}</td>
                                  <td className="apt-comment-cell">
                                    {renderComment(item.description, "prospect")}
                                  </td>
                                  <td className="apt-comment-cell">
                                    {renderComment(item.managerComments, "manager")}
                                  </td>
                                  <td className="apt-notes-cell">{renderNotes(item)}</td>
                                  <td>{renderStatusSelect(item)}</td>
                                  <td>{facts[1].value}</td>
                                  <td>{facts[0].value}</td>
                                  <td>{facts[2].value}</td>
                                  <td>{facts[3].value}</td>
                                  <td className="apt-actions-cell">{renderActions(item)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>

                  <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} centered>
                    <ModalHeader toggle={() => setConfirmModal(false)}>
                      <h3>Change Status</h3>
                    </ModalHeader>
                    <ModalBody><h5>Are you sure you want to change the status?</h5></ModalBody>
                    <ModalFooter>
                      <Button color="primary" onClick={changeStatus}>Yes</Button>
                      <Button color="light" onClick={() => setConfirmModal(false)}>No</Button>
                    </ModalFooter>
                  </Modal>

                  <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)}>
                    <ModalHeader style={{ border: "none" }} toggle={() => setRejectModal(false)}>
                      {rejectType === "rejected" ? "Reject Appointment" : "Cancel Appointment"}
                    </ModalHeader>
                    <ModalBody style={{ border: "none" }}>
                      <Label className="form-Label fw-semibold">Manager Comments</Label>
                      <textarea
                        rows={3}
                        placeholder={`Explain why this appointment is being ${rejectType === "rejected" ? "rejected" : "canceled"}…`}
                        className="form-control"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                      {reasonErr && <p className="text-danger mt-1">{reasonErr}</p>}
                    </ModalBody>
                    <ModalFooter style={{ border: "none" }}>
                      <Button color="primary" onClick={() => updateAppointment(rejectTarget, rejectType)}>Yes</Button>
                      <Button color="secondary" onClick={() => setRejectModal(false)}>No</Button>
                    </ModalFooter>
                  </Modal>

                  <Modal isOpen={acceptModal} toggle={() => !isAccepting && setAcceptModal(false)} size="lg">
                    <ModalHeader style={{ border: "none" }} toggle={() => !isAccepting && setAcceptModal(false)}>
                      Accept Appointment
                    </ModalHeader>
                    <ModalBody>
                      <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
                        You can optionally update the date and time before confirming.
                        Leave unchanged to keep the applicant's original request.
                        All times are in 15-minute intervals.
                      </p>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <Label className="form-Label fw-semibold">Date</Label>
                          <Input
                            type="date"
                            className="form-control"
                            value={overrideDate}
                            min={getTodayDenver()}
                            onChange={(e) => setOverrideDate(e.target.value)}
                          />
                        </div>

                        <div className="col-md-4">
                          <Label className="form-Label fw-semibold">
                            Start Time{" "}
                            {loadingSlots && (
                              <small className="text-muted">(loading…)</small>
                            )}
                          </Label>
                          <select
                            className="form-control"
                            disabled={loadingSlots || startTimeOptions.length === 0}
                            value={overrideStart}
                            onChange={(e) => setOverrideStart(e.target.value)}
                          >
                            {startTimeOptions.length === 0 && (
                              <option value="">
                                {loadingSlots
                                  ? "Loading…"
                                  : "No availability"}
                              </option>
                            )}
                            {startTimeOptions.map((hhmm) => {
                              const [h, m] = hhmm.split(":").map(Number);
                              const suffix = h >= 12 ? "PM" : "AM";
                              const d12 = ((h % 12) || 12).toString().padStart(2, "0");
                              return (
                                <option key={hhmm} value={hhmm}>
                                  {d12}:{String(m).padStart(2, "0")} {suffix}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        <div className="col-md-4">
                          <Label className="form-Label fw-semibold">End Time</Label>
                          <select
                            className="form-control"
                            disabled={!overrideStart || endTimeOptions.length === 0}
                            value={overrideEnd}
                            onChange={(e) => setOverrideEnd(e.target.value)}
                          >
                            {endTimeOptions.length === 0 && (
                              <option value="">
                                {overrideStart
                                  ? "Pick an earlier start"
                                  : "Select start first"}
                              </option>
                            )}
                            {endTimeOptions.map((hhmm) => {
                              const [h, m] = hhmm.split(":").map(Number);
                              const suffix = h >= 12 ? "PM" : "AM";
                              const d12 = ((h % 12) || 12).toString().padStart(2, "0");
                              return (
                                <option key={hhmm} value={hhmm}>
                                  {d12}:{String(m).padStart(2, "0")} {suffix}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        <div className="col-12">
                          <Label className="form-Label fw-semibold">Manager Comments</Label>
                          <textarea
                            rows={3}
                            className="form-control"
                            placeholder="Add a note about this appointment (optional)…"
                            value={acceptManagerComments}
                            onChange={(e) => setAcceptManagerComments(e.target.value)}
                          />
                        </div>

                        {(outlookConnected || !disableSyncButton) && (
                          <div className="col-12 d-none">
                            <div
                              style={{
                                background: "#f0f7ff",
                                border: "1px solid #d6e8fb",
                                borderRadius: 8,
                                padding: "10px 14px",
                                fontSize: "0.82rem",
                                color: "#1d4e89",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span style={{ fontSize: "1rem" }}>🔗</span>
                              <span>
                                On confirm, this appointment will auto-sync to
                                {!disableSyncButton && <strong> Google Calendar</strong>}
                                {!disableSyncButton && outlookConnected && " and"}
                                {outlookConnected && <strong> Outlook Calendar</strong>}
                                .
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </ModalBody>

                    <ModalFooter style={{ border: "none" }}>
                      <Button
                        color="success"
                        disabled={isAccepting}
                        onClick={() => submitAccept()}
                      >
                        {isAccepting ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Confirming…
                          </>
                        ) : (
                          "Confirm"
                        )}
                      </Button>

                      <Button
                        color="secondary"
                        disabled={isAccepting}
                        onClick={() => setAcceptModal(false)}
                      >
                        Cancel
                      </Button>
                    </ModalFooter>
                  </Modal>

                  <Modal
                    isOpen={textModal}
                    toggle={() => !sendingText && setTextModal(false)}
                    size="lg"
                  >
                    <ModalHeader
                      style={{ border: "none" }}
                      toggle={() => !sendingText && setTextModal(false)}
                    >
                      Send Text to Prospect
                    </ModalHeader>
                    <ModalBody>
                      {(() => {
                        if (!textTarget) return null;
                        const alreadySent =
                          textTarget.textSent ||
                          (textTarget.textHistory?.length || 0) > 0;
                        const templates = buildTextTemplates(textTarget);
                        const propertyLink = buildPropertyLink(
                          textTarget?.companyDomain,
                          textTarget?.properties?.[0]?.title
                        );
                        return (
                          <>
                            <div className="apt-text-prospect">
                              <div>
                                <div className="apt-text-prospect-name">
                                  {textTarget.name || "Unknown"}
                                </div>
                                <div className="apt-text-prospect-meta">
                                  ☎ {textTarget.phone || "—"}
                                  {textTarget.properties?.[0]?.title && (
                                    <> · 🏠 {textTarget.properties[0].title}</>
                                  )}
                                </div>
                                {propertyLink && (
                                  <a
                                    href={propertyLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="apt-text-prospect-link"
                                  >
                                    {propertyLink}
                                  </a>
                                )}
                              </div>
                              {alreadySent && (
                                <span className="apt-text-sent-badge">
                                  ✓ Already texted
                                </span>
                              )}
                            </div>

                            {alreadySent ? (
                              <div className="apt-text-history">
                                <div className="apt-text-history-title">
                                  Text History
                                </div>
                                {(textTarget.textHistory || []).map((t, i) => (
                                  <div className="apt-text-history-item" key={i}>
                                    <div className="apt-text-history-meta">
                                      <span>
                                        {t.sentByName || "Manager"} → {t.sentTo}
                                      </span>
                                      <span>{formatHistoryTime(t.sentAt)}</span>
                                    </div>
                                    <div className="apt-text-history-body">
                                      {t.message}
                                    </div>
                                  </div>
                                ))}
                                <p className="apt-text-history-note">
                                  Only one text per prospect is allowed.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div className="apt-text-templates">
                                  <Label className="form-Label fw-semibold mb-2">
                                    Choose a template (then edit as needed)
                                  </Label>
                                  <div className="apt-template-chips">
                                    {templates.map((t, i) => (
                                      <button
                                        key={i}
                                        type="button"
                                        className={`apt-template-chip${
                                          textTemplateIdx === i ? " is-active" : ""
                                        }`}
                                        onClick={() => handleTemplateChange(i)}
                                      >
                                        {t.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <Label className="form-Label fw-semibold mt-3">
                                  Message
                                </Label>
                                <textarea
                                  rows={8}
                                  className="form-control apt-text-body"
                                  value={textBody}
                                  onChange={(e) => setTextBody(e.target.value)}
                                  placeholder="Write a friendly message…"
                                />
                                <div className="apt-text-meta-row">
                                  <span>{textBody.length} characters</span>
                                  <span className="apt-text-warn">
                                    Only one text per prospect — choose carefully.
                                  </span>
                                </div>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </ModalBody>
                    <ModalFooter style={{ border: "none" }}>
                      {textTarget &&
                        !(
                          textTarget.textSent ||
                          (textTarget.textHistory?.length || 0) > 0
                        ) && (
                          <Button
                            color="primary"
                            disabled={sendingText || !textBody.trim()}
                            onClick={sendText}
                          >
                            {sendingText ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Sending…
                              </>
                            ) : (
                              "Send Text"
                            )}
                          </Button>
                        )}
                      <Button
                        color="secondary"
                        disabled={sendingText}
                        onClick={() => setTextModal(false)}
                      >
                        Close
                      </Button>
                    </ModalFooter>
                  </Modal>

                  <Row className="mt-3">
                    <Col xl={{ size: 9, offset: 3 }}>
                      <div className="pagination-bar text-end">
                        <Pagination>
                          <PaginationItem disabled={pageNumber === 1}>
                            <PaginationLink first onClick={() => handlePageChange(1)} />
                          </PaginationItem>
                          <PaginationItem disabled={pageNumber === 1}>
                            <PaginationLink previous onClick={() => handlePageChange(pageNumber - 1)} />
                          </PaginationItem>
                          {Array.from({ length: PAGE_LIMIT }, (_, i) => i + startPage).map((page) => {
                            if (page > pageTotal) return null;
                            return (
                              <PaginationItem key={page} active={pageNumber === page}>
                                <PaginationLink onClick={() => handlePageChange(page)}>
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          })}
                          <PaginationItem disabled={pageNumber === pageTotal}>
                            <PaginationLink next onClick={() => handlePageChange(pageNumber + 1)} />
                          </PaginationItem>
                          <PaginationItem disabled={pageNumber === pageTotal}>
                            <PaginationLink last onClick={() => handlePageChange(pageTotal)} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </Col>
                  </Row>

                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
      <style>{APPOINTMENTS_CSS}</style>
    </React.Fragment>
  );
};

const APPOINTMENTS_CSS = `
.apt-header {
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    padding: 22px 24px 18px;
}
.apt-header-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 18px;
    gap: 16px;
}
.apt-eyebrow {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.apt-title {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin: 4px 0 0;
    letter-spacing: -0.01em;
}
.apt-filter-toggle {
    position: relative;
    padding: 8px 14px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #475569;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.apt-filter-toggle:hover {
    background: #e2e8f0;
    color: #1e293b;
}
.apt-filter-toggle.is-open {
    background: #1e293b;
    color: #ffffff;
    border-color: #1e293b;
}
.apt-filter-icon { font-size: 14px; }
.apt-filter-dot {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
    box-shadow: 0 0 0 2px #ffffff;
}

.apt-stats {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 8px;
}
.apt-stat-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid rgba(15, 23, 42, 0.04);
    transition: transform 0.12s, box-shadow 0.12s;
}
.apt-stat-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.06);
}
.apt-stat-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.apt-stat-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1.2;
}
.apt-stat-count {
    font-size: 17px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.1;
    margin-top: 1px;
}

.apt-filter-panel {
    margin-top: 14px;
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px 18px 14px;
    box-shadow: inset 0 1px 0 #ffffff;
}
.apt-filter-label {
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin-bottom: 5px;
    display: block;
}
.apt-filter-panel .form-control,
.apt-filter-panel .form-control-sm {
    height: 36px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    padding: 7px 12px;
    background: #ffffff;
    transition: border-color 0.12s, box-shadow 0.12s;
}
.apt-filter-panel .form-control:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
    outline: none;
}
.apt-filter-panel input[type="date"] {
    color: #1e293b;
    font-weight: 500;
}
.apt-filter-panel .css-13cymwt-control,
.apt-filter-panel [class*="-control"] {
    min-height: 36px !important;
    border-radius: 8px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: none !important;
    background: #ffffff !important;
    transition: border-color 0.12s, box-shadow 0.12s !important;
}
.apt-filter-panel [class*="-control"]:hover {
    border-color: #cbd5e1 !important;
}
.apt-filter-panel [class*="-control"]--is-focused {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12) !important;
}
.apt-filter-panel [class*="-placeholder"] {
    color: #94a3b8 !important;
    font-size: 13px !important;
}
.apt-filter-panel [class*="-singleValue"] {
    color: #1e293b !important;
    font-size: 13px !important;
    font-weight: 500;
}

.apt-btn {
    padding: 8px 16px;
    border-radius: 8px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.12s;
}
.apt-btn--primary {
    background: #1e293b;
    color: #ffffff;
    border-color: #1e293b;
}
.apt-btn--primary:hover { background: #0f172a; }
.apt-btn--ghost {
    background: transparent;
    color: #64748b;
    border-color: #e2e8f0;
}
.apt-btn--ghost:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.apt-body { padding: 0; background: #fafbfc; }
.apt-table-wrap {
    border-top: 1px solid #f1f5f9;
    overflow-x: auto;
    overflow-y: visible;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
}
.apt-table-wrap::-webkit-scrollbar {
    height: 12px;
}
.apt-table-wrap::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
    margin: 0 4px;
}
.apt-table-wrap::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;
    border: 2px solid #f1f5f9;
}
.apt-table-wrap::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.apt-table {
    width: 100%;
    min-width: 1780px;
    background: #ffffff;
    border-collapse: separate;
    border-spacing: 0;
}
.apt-table thead th {
    background: #f8fafc;
    padding: 11px 14px;
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-bottom: 1px solid #e2e8f0;
    text-align: left;
    white-space: nowrap;
}
.apt-table tbody td {
    padding: 14px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: top;
    font-size: 13px;
    color: #1e293b;
    background: #ffffff;
    transition: background 0.12s;
}
.apt-table tbody tr:hover td {
    background: #fafbfc;
}
.apt-table tbody tr:last-child td { border-bottom: none; }

.apt-table th:last-child,
.apt-table td:last-child {
    position: sticky;
    right: 0;
    z-index: 2;
    box-shadow: -6px 0 12px -8px rgba(15, 23, 42, 0.15);
}
.apt-table thead th:last-child {
    background: #f8fafc;
    z-index: 3;
}
.apt-table tbody td:last-child {
    background: #ffffff;
}
.apt-table tbody tr:hover td:last-child {
    background: #fafbfc;
}

.apt-table th:nth-child(1),
.apt-table td:nth-child(1) {
    position: sticky;
    left: 0;
    z-index: 2;
    width: 56px;
    min-width: 56px;
}
.apt-table th:nth-child(2),
.apt-table td:nth-child(2) {
    position: sticky;
    left: 56px;
    z-index: 2;
    width: 240px;
    min-width: 240px;
}
.apt-table th:nth-child(3),
.apt-table td:nth-child(3) {
    position: sticky;
    left: 296px;
    z-index: 2;
    width: 200px;
    min-width: 200px;
    box-shadow: 6px 0 12px -8px rgba(15, 23, 42, 0.15);
}
.apt-table thead th:nth-child(1),
.apt-table thead th:nth-child(2),
.apt-table thead th:nth-child(3) {
    background: #f8fafc;
    z-index: 3;
}
.apt-table tbody td:nth-child(1),
.apt-table tbody td:nth-child(2),
.apt-table tbody td:nth-child(3) {
    background: #ffffff;
}
.apt-table tbody tr:hover td:nth-child(1),
.apt-table tbody tr:hover td:nth-child(2),
.apt-table tbody tr:hover td:nth-child(3) {
    background: #fafbfc;
}

.apt-cell-num {
    font-weight: 700;
    color: #94a3b8;
    font-size: 12px;
    text-align: center;
}

.apt-prospect {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 200px;
}
.apt-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #ffffff;
    font-weight: 700;
    font-size: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    letter-spacing: 0.02em;
}
.apt-prospect-info { min-width: 0; }
.apt-prospect-name {
    font-weight: 700;
    color: #0f172a;
    font-size: 13px;
    margin-bottom: 2px;
}
.apt-prospect-meta {
    font-size: 11.5px;
    color: #64748b;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
}

.apt-property-name {
    font-weight: 600;
    color: #0f172a;
    font-size: 12.5px;
}
.apt-property-layout {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 2px;
}

.apt-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.5;
}
.apt-pill--neutral {
    background: #eef2ff;
    color: #4338ca;
}

.apt-empty { color: #cbd5e1; font-size: 13px; }

.apt-comment-cell { min-width: 180px; max-width: 260px; }
.apt-comment {
    font-size: 12.5px;
    line-height: 1.5;
    white-space: pre-wrap;
    padding: 8px 10px;
    border-radius: 8px;
    border-left: 3px solid;
    max-height: 96px;
    overflow-y: auto;
}
.apt-comment--prospect {
    background: #f8fafc;
    color: #1e293b;
    border-left-color: #94a3b8;
}
.apt-comment--manager {
    background: #fefce8;
    color: #713f12;
    border-left-color: #eab308;
    font-style: italic;
}

.apt-actions-cell {
    display: flex !important;
    flex-direction: column;
    gap: 6px;
    min-width: 120px;
}
.apt-action {
    padding: 6px 12px;
    border-radius: 8px;
    border: none;
    font-size: 11.5px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.12s;
    letter-spacing: 0.02em;
    text-align: center;
    display: inline-block;
    line-height: 1.3;
}
.apt-action--accept {
    background: #10b981;
    color: #ffffff;
    box-shadow: 0 1px 3px rgba(16, 185, 129, 0.3);
}
.apt-action--accept:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(16, 185, 129, 0.35);
}
.apt-action--reject {
    background: #ef4444;
    color: #ffffff;
    box-shadow: 0 1px 3px rgba(239, 68, 68, 0.3);
}
.apt-action--reject:hover {
    background: #dc2626;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(239, 68, 68, 0.35);
}
.apt-action--booked {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
    cursor: default;
}
.apt-action--disabled {
    background: #f1f5f9;
    color: #64748b;
    cursor: not-allowed;
    text-transform: capitalize;
    border: 1px solid #e2e8f0;
}
.apt-action--text {
    background: #6366f1;
    color: #ffffff;
    box-shadow: 0 1px 3px rgba(99, 102, 241, 0.3);
}
.apt-action--text:hover {
    background: #4f46e5;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(99, 102, 241, 0.35);
}
.apt-action--text-sent {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
    box-shadow: none;
}
.apt-action--text-sent:hover {
    background: #d1fae5;
    transform: none;
    box-shadow: none;
}

.apt-notes-cell {
    min-width: 240px;
    max-width: 280px;
}
.apt-notes-box {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.apt-notes-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 140px;
    overflow-y: auto;
}
.apt-note-item {
    background: #f8fafc;
    border-left: 3px solid #6366f1;
    border-radius: 6px;
    padding: 6px 8px;
}
.apt-note-meta {
    display: flex;
    justify-content: space-between;
    font-size: 10.5px;
    color: #64748b;
    margin-bottom: 2px;
    gap: 6px;
}
.apt-note-author {
    font-weight: 700;
    color: #475569;
}
.apt-note-time {
    white-space: nowrap;
}
.apt-note-text {
    font-size: 12px;
    color: #1e293b;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
}
.apt-note-toggle {
    background: transparent;
    border: none;
    color: #6366f1;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 0;
    cursor: pointer;
    align-self: flex-start;
}
.apt-note-toggle:hover {
    text-decoration: underline;
}
.apt-note-editor {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.apt-note-input {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 6px 8px;
    font-size: 12px;
    color: #1e293b;
    resize: vertical;
    min-height: 40px;
    background: #ffffff;
}
.apt-note-input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}
.apt-note-save {
    align-self: flex-end;
    padding: 4px 10px;
    background: #1e293b;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.12s;
}
.apt-note-save:hover:not(:disabled) {
    background: #0f172a;
}
.apt-note-save:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
}

.apt-text-prospect {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: #f8fafc;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 14px;
    gap: 12px;
}
.apt-text-prospect-name {
    font-weight: 700;
    color: #0f172a;
    font-size: 14px;
}
.apt-text-prospect-meta {
    font-size: 12.5px;
    color: #64748b;
    margin-top: 2px;
}
.apt-text-prospect-link {
    display: inline-block;
    margin-top: 4px;
    font-size: 12px;
    color: #6366f1;
    word-break: break-all;
}
.apt-text-sent-badge {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11.5px;
    font-weight: 700;
    white-space: nowrap;
    align-self: flex-start;
}
.apt-template-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.apt-template-chip {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #475569;
    border-radius: 999px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.12s;
}
.apt-template-chip:hover {
    border-color: #6366f1;
    color: #4338ca;
}
.apt-template-chip.is-active {
    background: #4338ca;
    color: #ffffff;
    border-color: #4338ca;
}
.apt-text-body {
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.5;
}
.apt-text-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 6px;
    font-size: 11.5px;
    color: #64748b;
}
.apt-text-warn {
    color: #b45309;
    font-weight: 600;
}
.apt-text-history {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.apt-text-history-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.apt-text-history-item {
    background: #f0f7ff;
    border-left: 3px solid #6366f1;
    border-radius: 8px;
    padding: 10px 12px;
}
.apt-text-history-meta {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #475569;
    margin-bottom: 4px;
    gap: 8px;
    flex-wrap: wrap;
}
.apt-text-history-body {
    font-size: 13px;
    color: #1e293b;
    white-space: pre-wrap;
    line-height: 1.5;
}
.apt-text-history-note {
    font-size: 11.5px;
    color: #94a3b8;
    margin: 4px 0 0;
    font-style: italic;
}

.apt-prospect-link {
    color: inherit;
    text-decoration: none;
}
.apt-prospect-link:hover {
    color: #4338ca;
    text-decoration: underline;
}

/* ── Compact layout (tablet / narrow windows): one card per appointment ──
   Rendered instead of the table when the list container is narrower than
   COMPACT_BREAKPOINT, so nothing is pinned and nothing scrolls sideways. */
.apt-list { min-width: 0; }
.apt-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px 18px;
}
.apt-cards-empty {
    padding: 32px 16px;
    text-align: center;
    color: #94a3b8;
    font-size: 13.5px;
    background: #ffffff;
    border: 1px dashed #e2e8f0;
    border-radius: 14px;
}
.apt-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    padding: 14px 16px 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.apt-card-head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
}
.apt-card-num {
    font-weight: 700;
    color: #94a3b8;
    font-size: 12px;
    padding-top: 10px;
    min-width: 18px;
}
.apt-card-identity {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.apt-card .apt-prospect { min-width: 0; }
.apt-card .apt-prospect-name { font-size: 15px; }
.apt-card .apt-prospect-meta {
    font-size: 13px;
    white-space: normal;
    overflow: visible;
    max-width: none;
    line-height: 1.6;
}
.apt-card-property {
    padding-left: 46px; /* align under the name, past the avatar */
}
.apt-card .apt-property-name { font-size: 13.5px; }
.apt-card .apt-property-layout { font-size: 12px; }
.apt-card-status { padding-top: 2px; }

.apt-card-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 28px;
    margin: 12px 0 0;
    padding: 10px 0;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
}
.apt-fact { min-width: 0; }
.apt-fact dd { white-space: nowrap; }
.apt-fact dt {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin: 0 0 3px;
}
.apt-fact dd {
    margin: 0;
    font-size: 13px;
    color: #1e293b;
    font-weight: 600;
    line-height: 1.4;
}

.apt-card-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px 24px;
    padding: 12px 0 4px;
}
.apt-card-section {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.apt-card-label {
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin: 4px 0 0;
}
.apt-card .apt-comment {
    max-height: none;       /* read the whole thing; no nested scroll on touch */
    font-size: 13px;
    padding: 10px 12px;
}
.apt-card .apt-notes-list { max-height: 260px; }
.apt-card .apt-note-text { font-size: 13px; }
.apt-card .apt-note-input {
    min-height: 56px;
    font-size: 13px;
    padding: 8px 10px;
}
.apt-card .apt-note-save {
    min-height: 36px;
    padding: 6px 16px;
    font-size: 12px;
}
.apt-card .apt-note-toggle {
    min-height: 32px;
    font-size: 12px;
}

.apt-card-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
}
.apt-card-actions .apt-action {
    min-height: 40px;
    padding: 8px 18px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.apt-card-actions .apt-action--text { margin-left: auto; }

@media (max-width: 720px) {
    .apt-cards { padding: 12px; }
    .apt-card { padding: 12px 14px 10px; }
    .apt-card-head { grid-template-columns: auto minmax(0, 1fr); }
    .apt-card-status { grid-column: 2; padding-top: 0; }
    .apt-card-property { padding-left: 0; }
    .apt-card-facts { gap: 8px 20px; }
    .apt-fact { flex: 1 1 40%; }
    .apt-card-body { grid-template-columns: 1fr; }
    .apt-card-actions .apt-action { flex: 1 1 auto; }
    .apt-card-actions .apt-action--text { margin-left: 0; }
}

/* Touch devices: bigger targets in the header, filters and pagination. */
@media (pointer: coarse) {
    .apt-filter-toggle { padding: 11px 16px; font-size: 14px; }
    .apt-filter-panel .form-control,
    .apt-filter-panel .form-control-sm { height: 42px; font-size: 14px; }
    .apt-filter-panel [class*="-control"] { min-height: 42px !important; }
    .apt-filter-panel [class*="-placeholder"],
    .apt-filter-panel [class*="-singleValue"] { font-size: 14px !important; }
    .apt-filter-actions .apt-btn { min-height: 42px; padding: 10px 18px; font-size: 14px; }
    .apt-body .page-link {
        min-width: 40px;
        min-height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .apt-table .apt-action { min-height: 36px; }
}

@media (max-width: 1200px) {
    .apt-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 768px) {
    .apt-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .apt-header { padding: 18px 16px 14px; }
}
@media (max-width: 480px) {
    .apt-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; }
    .apt-stat-card { padding: 8px 10px; gap: 8px; }
    .apt-stat-icon { width: 26px; height: 26px; font-size: 12px; }
    .apt-stat-count { font-size: 15px; }
}
`;

export default Appointment;